<template>
  <div v-if="model.enabled" class="module-navigation"
    :class="{ 'is-edit': editing, 'is-mobile': device === DeviceEnum.MOBILE, 'at-right': isAtRight }"
    :style="colorVars">
    <transition name="module-navigation-fade">
      <div v-if="panelVisible" class="module-navigation__overlay" :class="{ 'is-edit': editing }" :style="overlayStyle" />
    </transition>
    <transition name="module-navigation-panel">
      <div v-if="panelVisible" class="module-navigation__panel" :style="panelStyle">
        <bg-style :bg="model.background" class="module-navigation__panel-inner" :style="globalStyle">
          <div class="panel-title">{{ $t('siteBuild.moduleNavigationWidget.panelTitle') }}</div>
          <div class="menu-list">
            <div v-for="(item, index) in menuList" :key="item.id" class="menu-item"
              :class="{ active: activeIndex === index }" @click.stop="handleToModule(item, index)">
              <span class="menu-item-icon"><im-icon :icon="item.icon" /></span>
              <span class="menu-item-text">{{ item.title }}</span>
              <img v-if="item.imgSrc" :src="item.imgSrc" class="menu-item-img" alt="" />
            </div>
          </div>
          <div class="menu-sep" />
          <div class="menu-item back-top" @click.stop="handleToTop">
            <span class="menu-item-icon back-top-icon"><i class="el-icon-arrow-up" /></span>
            <span class="menu-item-text">{{ $t('siteBuild.moduleNavigationWidget.backToTop') }}</span>
          </div>
        </bg-style>
      </div>
    </transition>
    <div class="module-navigation__dot" :class="{ 'panel-active': panelVisible, dragging: isDragging, snapping: isSnapping }" :style="dotStyle" @mousedown.prevent="onPointerDown" @touchstart.prevent="onPointerDown">
      <img v-if="model.icon?.backgroundImage" :src="model.icon.backgroundImage" class="dot-custom-icon" alt="" />
      <i v-else :class="panelVisible ? 'el-icon-close' : 'el-icon-menu'" class="dot-icon" />
    </div>
    <span v-if="!isDragging && !panelVisible" class="module-navigation__pulse" :class="{ 'at-right': isAtRight }"
      :style="pulsePositionStyle" />
  </div>
</template>

<script>
import { unescape, debounce, cloneDeep } from 'lodash-es'
import { DeviceEnum } from '~/enums/deviceEnum'
import { WidgetModelNames } from '~/enums/siteEnum'
import { sleep } from '~/utils'
import { scrollTo, on, off } from '~/utils/dom'
import { getTextFromHtmlStr } from '~/utils/htmlStringHandler'

const getFirstImgSrc = (htmlStr = '') => {
  if (!htmlStr) return ''
  const match = htmlStr.match(/<img[^>]+src=["']([^"']+)["']/i)
  return match ? match[1] : ''
}

const DOT_SIZE = 42
const SNAP_MARGIN = 12
const DRAG_THRESHOLD = 5
const FIRST_VISIT_KEY = 'module_nav_visited'
const DOT_POS_KEY = 'module_nav_dot_pos'

export default {
  name: 'ModuleNavigationWidget',
  props: {
    editing: {
      type: Boolean,
      default: false,
    },
    device: {
      type: String,
      default: DeviceEnum.DESKTOP,
    },
    site: {
      type: Object,
      default() {
        return {}
      },
    },
    model: {
      type: Object,
      default() {
        return {}
      },
    },
  },
  data() {
    return {
      panelVisible: false,
      activeIndex: -1,
      debounceScroll: '',
      isDragging: false,
      isSnapping: false,
      dragStarted: false,
      startPointerX: 0,
      startPointerY: 0,
      startDotX: 0,
      startDotY: 0,
      dotX: -DOT_SIZE / 2,
      dotY: 0,
      offsetLeft: 0,
      editAreaWidth: window.innerWidth,
      editAreaHeight: window.innerHeight,
      editAreaOffset: { x: 0, y: 0 },
    }
  },
  computed: {
    DeviceEnum() {
      return DeviceEnum
    },
    globalStyle() {
      return this.site.globalSetting.__vars
    },
    menuList() {
      return this.site.widgetList.map((item) => {
        const { icon, isDefault, id } = item.getMenuInfo()
        const cleanHtml = (item.title || '').replace(/&(?:nbsp|ensp|emsp|thinsp|zwnj|zwj);/gi, '')
        const title = unescape(getTextFromHtmlStr(cleanHtml))
        const imgSrc = getFirstImgSrc(item.title)
        const visibleJude = item.title && item.titleVisible
        return {
          title,
          icon,
          isDefault,
          id,
          imgSrc,
          name: item.name,
          hide: item.__hide,
          visible: visibleJude && Boolean(item.visible),
        }
      }).filter((item) => item.visible && !item.hide)
    },
    containerClass() {
      return this.editing ? '.edit-content__center--wrapper' : 'site-body'
    },
    fixedHeaderOffset() {
      const header = this.site.widgetList.find((w) => w.name === WidgetModelNames.HeaderWidgetModel)
      if (!header?.fixed) return 0
      const dom = document.querySelector('.wrapper.is-fixed')
      return dom ? dom.offsetHeight : 0
    },
    isAtRight() {
      const refWidth = this.editing ? this.editAreaWidth : window.innerWidth
      return this.dotX + DOT_SIZE / 2 > refWidth / 2
    },
    dotStyle() {
      const offset = this.editAreaOffset
      const style = {
        left: (this.editing ? this.dotX + offset.x : this.dotX) + 'px',
        top: (this.editing ? this.dotY + offset.y : this.dotY) + 'px',
      }
      if (this.editing) {
        // 编辑模式下裁切超出编辑区域边缘的一半
        if (this.isAtRight) {
          style.clipPath = `inset(-3px ${DOT_SIZE / 2}px -3px -3px)`
        } else {
          style.clipPath = `inset(-3px -3px -3px ${DOT_SIZE / 2}px)`
        }
      }
      return style
    },
    panelStyle() {
      const panelW = this.device === DeviceEnum.MOBILE ? 210 : 240
      const gap = 10
      const offset = this.editAreaOffset
      let left
      if (this.isAtRight) {
        left = this.editing ? this.dotX - panelW - gap + offset.x : this.dotX - panelW - gap
      } else {
        left = this.editing ? this.dotX + DOT_SIZE + gap + offset.x : this.dotX + DOT_SIZE + gap
      }
      const refHeight = this.editing ? this.editAreaHeight : window.innerHeight
      const panelH = 300
      let top = this.dotY + DOT_SIZE / 2 - panelH / 2
      top = Math.max(SNAP_MARGIN, Math.min(refHeight - panelH - SNAP_MARGIN, top))
      if (this.editing) top += offset.y
      return { left: left + 'px', top: top + 'px' }
    },
    colorVars() {
      const active = this.model.activeColor?.backgroundColor || '#6366f1'
      const inactive = this.model.inactiveColor?.backgroundColor || '#9ca3af'
      const rgb = this.hexToRgb(active)
      return {
        '--nav-active-color': active,
        '--nav-active-color-rgb': rgb,
        '--nav-inactive-color': inactive,
      }
    },
    overlayStyle() {
      if (!this.editing) return {}
      const offset = this.editAreaOffset
      return {
        left: offset.x + 'px',
        top: offset.y + 'px',
        width: this.editAreaWidth + 'px',
        height: this.editAreaHeight + 'px',
      }
    },
    pulsePositionStyle() {
      const offset = this.editing ? this.editAreaOffset : { x: 0, y: 0 }
      const left = this.editing ? this.dotX + offset.x : this.dotX
      const top = this.editing ? this.dotY + offset.y : this.dotY
      // 呼吸灯在按钮朝内的一侧：atRight 时在按钮左侧，否则在右侧
      const pulseLeft = this.isAtRight ? left - 6 : left + DOT_SIZE - 3
      return {
        left: pulseLeft + 'px',
        top: top + DOT_SIZE / 2 + 'px',
      }
    },
  },
  watch: {
    device(n) {
      if (n === DeviceEnum.MOBILE) {
        this.getStyle()
      }
      const timer = setTimeout(() => {
        this.updateEditAreaSize()
        if (this.editing) {
          this.initDotPosition()
        }
        clearTimeout(timer)
      }, 500)
    },
  },
  mounted() {
    this.updateEditAreaSize()
    this.initDotPosition()
    this.debounceScroll = debounce(this.checkScroll, 300)
    const siteBody = document.getElementById('site-body')
    if (siteBody) {
      on(siteBody, 'scroll', this.debounceScroll)
    }
    on(document, 'mousemove', this.onPointerMove)
    on(document, 'mouseup', this.onPointerUp)
    on(document, 'touchmove', this.onPointerMove)
    on(document, 'touchend', this.onPointerUp)
    on(window, 'resize', this.onResize)
    // 首次访问自动展开
    this.handleFirstVisit()
  },
  beforeDestroy() {
    const siteBody = document.getElementById('site-body')
    if (siteBody) {
      off(siteBody, 'scroll', this.debounceScroll)
    }
    off(document, 'mousemove', this.onPointerMove)
    off(document, 'mouseup', this.onPointerUp)
    off(document, 'touchmove', this.onPointerMove)
    off(document, 'touchend', this.onPointerUp)
    off(window, 'resize', this.onResize)
    document.removeEventListener('mousedown', this.onOutsideClick)
    document.removeEventListener('touchstart', this.onOutsideClick)
  },
  methods: {
    checkModuleInMenuManage(item) {
      const selfId = item.id
      const menuManageWidgets = this.site.widgetList.filter((w) => w.name === WidgetModelNames.MenuManageWidgetModel)
      const menuItem = menuManageWidgets.find((w) => w.navs.find((n) => n.models?.includes(selfId)))
      return menuItem?.id
    },
    checkScroll() {
      const scrollTop = document.getElementById('site-body')?.scrollTop
      const menuList = cloneDeep(this.menuList)
      menuList.forEach((item) => {
        const newItem = { ...item }
        const dom = document.getElementById(newItem.id) || {}
        item.offsetTop = dom.offsetTop - dom.offsetHeight / 2
      })
      for (let i = 0; i < menuList.length; i++) {
        const item = menuList[i]
        if (scrollTop >= item.offsetTop) {
          this.activeIndex = i
        }
      }
      if (scrollTop === 0) {
        this.activeIndex = 0
      }
    },
    async getStyle() {
      if (!this.editing) return
      await sleep(500)
      const $dom = document.querySelector('.edit-content__center')
      this.offsetLeft = `${$dom.offsetLeft}px`
    },
    updateEditAreaSize() {
      if (!this.editing) return
      const $center = document.querySelector('.edit-content__center')
      if ($center) {
        this.editAreaWidth = $center.offsetWidth
        this.editAreaHeight = $center.offsetHeight
        const rect = $center.getBoundingClientRect()
        this.editAreaOffset = { x: rect.left, y: rect.top }
      }
    },
    handleToModule(item, index) {
      const menuManageId = this.checkModuleInMenuManage(item)
      scrollTo(menuManageId || item.id, this.containerClass, this.fixedHeaderOffset)
      this.activeIndex = index
      this.closePanel()
    },
    handleToTop() {
      const $container = this.editing
        ? document.querySelector(this.containerClass)
        : document.getElementById(this.containerClass)
      $container.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
      this.closePanel()
    },
    // ---- 面板 ----
    openPanel() {
      this.panelVisible = true
      document.addEventListener('mousedown', this.onOutsideClick)
      document.addEventListener('touchstart', this.onOutsideClick)
    },
    closePanel() {
      this.panelVisible = false
      document.removeEventListener('mousedown', this.onOutsideClick)
      document.removeEventListener('touchstart', this.onOutsideClick)
    },
    togglePanel() {
      this.panelVisible ? this.closePanel() : this.openPanel()
    },
    onOutsideClick(e) {
      const panel = this.$el.querySelector('.module-navigation__panel')
      const dot = this.$el.querySelector('.module-navigation__dot')
      if (panel?.contains(e.target) || dot?.contains(e.target) || this.editing) return
      this.closePanel()
    },
    // ---- 首次访问 ----
    handleFirstVisit() {
      if (this.editing) return
      const visited = localStorage.getItem(FIRST_VISIT_KEY)
      if (!visited) {
        setTimeout(() => {
          this.openPanel()
          setTimeout(() => {
            this.closePanel()
          }, 2000)
          localStorage.setItem(FIRST_VISIT_KEY, '1')
        }, 1000)
      }
    },
    // ---- 颜色工具 ----
    hexToRgb(hex) {
      const shorthand = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
      hex = hex.replace(shorthand, (m, r, g, b) => r + r + g + g + b + b)
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
      if (!result) return '99,102,241'
      return `${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(result[3], 16)}`
    },
    // ---- 悬浮按钮位置 ----
    initDotPosition() {
      if (this.editing) {
        const saved = this.loadDotPos()
        if (saved) {
          const maxY = this.editAreaHeight - DOT_SIZE - SNAP_MARGIN
          this.dotY = Math.max(SNAP_MARGIN, Math.min(maxY, saved.y))
          this.dotX = saved.x
          const centerX = this.dotX + DOT_SIZE / 2
          if (centerX < this.editAreaWidth / 2) {
            this.dotX = -DOT_SIZE / 2
          } else {
            this.dotX = this.editAreaWidth - DOT_SIZE / 2
          }
        } else {
          this.dotY = this.editAreaHeight / 2 - DOT_SIZE / 2
          this.dotX = this.editAreaWidth - DOT_SIZE / 2
        }
        return
      }
      const saved = this.loadDotPos()
      if (saved) {
        const maxY = window.innerHeight - DOT_SIZE - SNAP_MARGIN
        this.dotY = Math.max(SNAP_MARGIN, Math.min(maxY, saved.y))
        this.dotX = saved.x
        const centerX = this.dotX + DOT_SIZE / 2
        if (centerX < window.innerWidth / 2) {
          this.dotX = -DOT_SIZE / 2
        } else {
          this.dotX = window.innerWidth - DOT_SIZE / 2
        }
      } else {
        this.dotY = window.innerHeight / 2 - DOT_SIZE / 2
        this.dotX = window.innerWidth - DOT_SIZE / 2
      }
    },
    saveDotPos() {
      try {
        localStorage.setItem(DOT_POS_KEY, JSON.stringify({ x: this.dotX, y: this.dotY }))
      } catch (e) {
        // localStorage 不可用时静默忽略
      }
    },
    loadDotPos() {
      try {
        const raw = localStorage.getItem(DOT_POS_KEY)
        if (raw) {
          const pos = JSON.parse(raw)
          if (typeof pos.x === 'number' && typeof pos.y === 'number') return pos
        }
      } catch (e) {
        // 解析失败时忽略
      }
      return null
    },
    applyPosition(animate) {
      if (animate) {
        this.isSnapping = true
        setTimeout(() => {
          this.isSnapping = false
        }, 350)
      }
    },
    snapToEdge() {
      const refWidth = this.editing ? this.editAreaWidth : window.innerWidth
      const refHeight = this.editing ? this.editAreaHeight : window.innerHeight
      const centerX = this.dotX + DOT_SIZE / 2
      if (centerX < refWidth / 2) {
        this.dotX = -DOT_SIZE / 2
      } else {
        this.dotX = refWidth - DOT_SIZE / 2
      }
      this.dotY = Math.max(SNAP_MARGIN, Math.min(refHeight - DOT_SIZE - SNAP_MARGIN, this.dotY))
      this.applyPosition(true)
      this.saveDotPos()
    },
    // ---- 拖拽 ----
    onPointerDown(e) {
      if (this.panelVisible) {
        this.closePanel()
        return
      }
      this.isDragging = true
      this.dragStarted = false
      this.updateEditAreaSize()
      const point = e.touches ? e.touches[0] : e
      this.startPointerX = point.clientX
      this.startPointerY = point.clientY
      this.startDotX = this.dotX
      this.startDotY = this.dotY
    },
    onPointerMove(e) {
      if (!this.isDragging) return
      const point = e.touches ? e.touches[0] : e
      const dx = point.clientX - this.startPointerX
      const dy = point.clientY - this.startPointerY
      if (!this.dragStarted && Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD) {
        this.dragStarted = true
      }
      if (this.dragStarted) {
        e.preventDefault()
        this.dotX = this.startDotX + dx
        this.dotY = this.startDotY + dy
        const refWidth = this.editing ? this.editAreaWidth : window.innerWidth
        const refHeight = this.editing ? this.editAreaHeight : window.innerHeight
        this.dotX = Math.max(-DOT_SIZE, Math.min(refWidth - 4, this.dotX))
        this.dotY = Math.max(SNAP_MARGIN, Math.min(refHeight - DOT_SIZE - SNAP_MARGIN, this.dotY))
      }
    },
    onPointerUp() {
      if (!this.isDragging) return
      this.isDragging = false
      if (this.dragStarted) {
        this.snapToEdge()
      } else {
        this.togglePanel()
      }
    },
    onResize() {
      this.updateEditAreaSize()
      const refWidth = this.editing ? this.editAreaWidth : window.innerWidth
      const refHeight = this.editing ? this.editAreaHeight : window.innerHeight
      this.dotY = Math.max(SNAP_MARGIN, Math.min(refHeight - DOT_SIZE - SNAP_MARGIN, this.dotY))
      const centerX = this.dotX + DOT_SIZE / 2
      if (centerX < refWidth / 2) {
        this.dotX = -DOT_SIZE / 2
      } else {
        this.dotX = refWidth - DOT_SIZE / 2
      }
      this.saveDotPos()
    },
  },
}
</script>

<style lang="less">
.module-navigation {
  z-index: 4000;

  // ---- 遮罩 ----
  &__overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.08);
    z-index: 199;
    pointer-events: none;

    &.is-edit {
      inset: unset;
    }
  }

  // ---- 悬浮圆形按钮 ----
  &__dot {
    position: fixed;
    width: 42px;
    height: 42px;
    border-radius: 50%;
    background-color: #fff;
    border: 1px solid #e5e5e5;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: grab;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    z-index: 201;
    user-select: none;
    -webkit-user-select: none;
    touch-action: none;
    transition: background 0.25s, border-color 0.25s, box-shadow 0.25s, transform 0.2s;
    overflow: visible;

    .dot-icon {
      color: #9ca3af;
      transition: color 0.25s, transform 0.25s;
      pointer-events: none;
      font-size: 18px;
    }

    .dot-custom-icon {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 50%;
      pointer-events: none;
      transition: transform 0.25s;
    }

    &:hover,
    &.panel-active {
      border-color: color-mix(in srgb, var(--nav-active-color), transparent 20%);
      box-shadow: 0 2px 12px color-mix(in srgb, var(--nav-active-color), transparent 90%),
        0 0 0 3px color-mix(in srgb, var(--nav-active-color), transparent 70%);

      .dot-icon {
        color: var(--nav-active-color);
        transform: scale(1.1);
      }

      .dot-custom-icon {
        transform: scale(1.05);
      }
    }

    &.dragging {
      cursor: grabbing;
      transform: scale(1.15);
      box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
      transition: transform 0.15s, box-shadow 0.15s;
    }

    &.snapping {
      transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1), top 0.3s cubic-bezier(0.4, 0, 0.2, 1), background 0.25s,
        border-color 0.25s, box-shadow 0.25s;
    }
  }

  // 呼吸发光（兄弟元素，不受按钮 clip-path 裁切）
  &__pulse {
    position: fixed;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--nav-active-color);
    box-shadow: 0 0 8px rgba(99, 102, 241, 0.6);
    animation: dotPulse 2.5s ease-in-out infinite;
    transition: opacity 0.3s;
    pointer-events: none;
    z-index: 201;
  }

  // ---- 菜单面板 ----
  &__panel {
    position: fixed;
    width: 240px;
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(20px);
    border: 1px solid #e5e5e5;
    border-radius: 14px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
    z-index: 200;
    overflow: hidden;
  }

  &__panel-inner {
    padding: 10px 16px;

    .panel-title {
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      color: var(--nav-inactive-color);
      margin-bottom: 14px;
      padding-bottom: 10px;
      border-bottom: 1px solid #f0f0f0;
      position: relative;
    }

    .menu-list {
      display: flex;
      flex-direction: column;
      gap: 1px;
      max-height: 250px;
      overflow: auto;
    }

    .menu-item {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 8px 10px;
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.2s;
      position: relative;

      &-img {
        width: 18px;
        height: 18px;
        border-radius: 3px;
        object-fit: cover;
        flex-shrink: 0;
      }

      &-icon {
        width: 18px;
        height: 18px;
        flex-shrink: 0;
        transition: color 0.25s;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 15px;

        .im-icon {
          width: 15px;
          height: 15px;
        }

        &.back-top-icon {
          color: #c4c4c4;
          font-size: 14px;
        }
      }

      &-text {
        font-size: 13px;
        font-weight: 400;
        line-height: 1.4;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      // hover 时用选中色
      &:not(.active):not(.back-top):hover {
        color: var(--nav-active-color, #6366f1);
        background-color: color-mix(in srgb, var(--nav-active-color), transparent 90%);

        .menu-item-icon {
          color: var(--nav-active-color, #6366f1);
        }
      }

      // 默认未选中颜色
      &:not(.active):not(.back-top) {
        color: var(--nav-inactive-color);
      }

      // 左侧激活指示条
      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%) scaleY(0);
        width: 3px;
        height: 55%;
        border-radius: 2px;
        background: var(--nav-active-color);
        transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      }

      &.active {
        color: var(--nav-active-color);
        background-color: color-mix(in srgb, var(--nav-active-color), transparent 90%);
        font-weight: 500;

        .menu-item-icon {
          color: var(--nav-active-color);
        }

        &::before {
          transform: translateY(-50%) scaleY(1);
        }
      }

      &.back-top {
        color: #b4b4b4;
        padding: 0 10px;

        &:hover {
          color: var(--nav-active-color, #6366f1);
          background: none !important;

          .menu-item-icon {
            color: var(--nav-active-color, #6366f1);
          }
        }
      }
    }

    .menu-sep {
      height: 1px;
      background: #f0f0f0;
      margin: 10px 10px 8px;
      position: relative;
    }
  }

  // ---- 移动端 ----
  &.is-mobile {
    .module-navigation__dot {
      width: 38px;
      height: 38px;

      .dot-icon {
        font-size: 16px;
      }

      .dot-custom-icon {
        width: 100%;
        height: 100%;
      }
    }

    .module-navigation__panel {
      width: 210px;
    }

    &__panel-inner {
      padding: 16px 12px;
    }
  }

  // ---- 过渡动画 ----
  &-fade-enter-active,
  &-fade-leave-active {
    transition: opacity 0.3s;
  }

  &-fade-enter,
  &-fade-leave-to {
    opacity: 0;
  }

  &-panel-enter-active {
    transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &-panel-leave-active {
    transition: opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1), transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }

  // 面板在左侧时（按钮在右侧）：从右滑入
  &.at-right &-panel-enter,
  &.at-right &-panel-leave-to {
    opacity: 0;
    transform: translateX(12px) scale(0.96);
  }

  // 面板在右侧时（按钮在左侧）：从左滑入
  &:not(.at-right) &-panel-enter,
  &:not(.at-right) &-panel-leave-to {
    opacity: 0;
    transform: translateX(-12px) scale(0.96);
  }
}

@keyframes dotPulse {

  0%,
  100% {
    opacity: 0.3;
    transform: translateY(-50%) scale(0.8);
  }

  50% {
    opacity: 1;
    transform: translateY(-50%) scale(1.2);
  }
}
</style>
